import { useState, useEffect, useRef } from 'react';
import type { UserJob, SubcomponentType } from '../types';
import { api } from '../services/api';

interface UseJobPollingParams {
  jobs: UserJob[];
  loadJobs: (isBackgroundRefresh?: boolean) => Promise<void>;
  // Called once per polled job with the freshly-fetched record. Lets the
  // parent merge state changes (phase, state*) without a full list refetch.
  onPollingUpdate?: (job: UserJob) => void;
}

interface UseJobPollingResult {
  generatingTracking: Map<string, number>;
  setGeneratingTracking: React.Dispatch<React.SetStateAction<Map<string, number>>>;
}

const SUBCOMPONENTS: SubcomponentType[] = [
  'contact', 'summary', 'skills', 'highlights', 'experience', 'education',
  'awards', 'presentations', 'publications', 'portfolios', 'coverletter', 'elevatorpitch',
];

// Decide whether a job has at least one *non-stale* generating sub-state OR
// is in the Generating phase. Mirrors the rules in the previous version so
// behavior of the active-polling trigger doesn't change — only the work the
// poll does changes.
function isActivelyGenerating(
  job: UserJob,
  generatingTracking: Map<string, number>,
  pageLoadTime: number,
  now: number,
): boolean {
  if (job.jobphase === 'Generating') return true;
  const TIMEOUT_MS = 10 * 60 * 1000;
  const INITIAL_TIMEOUT_MS = 2 * 60 * 1000;
  return SUBCOMPONENTS.some((comp) => {
    const state = job[`state${comp}` as keyof UserJob] as string;
    if (state !== 'generating' && state !== 'retry') return false;
    const trackingKey = `${job.jobid}-${comp}`;
    const startTime = generatingTracking.get(trackingKey);
    if (!startTime) {
      // Was generating on page load — only trust it for 2 minutes
      return now - pageLoadTime <= INITIAL_TIMEOUT_MS;
    }
    return now - startTime <= TIMEOUT_MS;
  });
}

export default function useJobPolling({
  jobs,
  loadJobs,
  onPollingUpdate,
}: UseJobPollingParams): UseJobPollingResult {
  const [generatingTracking, setGeneratingTracking] = useState<Map<string, number>>(new Map());
  const [pageLoadTime] = useState<number>(Date.now());

  // Stable refs so the active-polling tick can read the latest values without
  // tearing down/recreating the interval on every poll-driven jobs update.
  const jobsRef = useRef(jobs);
  const trackingRef = useRef(generatingTracking);
  const onUpdateRef = useRef(onPollingUpdate);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);
  useEffect(() => { trackingRef.current = generatingTracking; }, [generatingTracking]);
  useEffect(() => { onUpdateRef.current = onPollingUpdate; }, [onPollingUpdate]);

  // Track generation start times and detect stale requests
  useEffect(() => {
    const now = Date.now();
    const TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

    jobs.forEach((job) => {
      SUBCOMPONENTS.forEach(comp => {
        const state = job[`state${comp}` as keyof UserJob] as string;
        const trackingKey = `${job.jobid}-${comp}`;

        if (state === 'generating' || state === 'retry') {
          if (!generatingTracking.has(trackingKey)) {
            setGeneratingTracking(prev => new Map(prev).set(trackingKey, now));
          } else {
            const startTime = generatingTracking.get(trackingKey);
            if (startTime && (now - startTime) > TIMEOUT_MS) {
              console.warn(`Generation timeout detected for ${job.jobid} ${comp} - started ${Math.round((now - startTime) / 1000)}s ago`);
              setGeneratingTracking(prev => {
                const next = new Map(prev);
                next.delete(trackingKey);
                return next;
              });
            }
          }
        } else if (generatingTracking.has(trackingKey)) {
          setGeneratingTracking(prev => {
            const next = new Map(prev);
            next.delete(trackingKey);
            return next;
          });
        }
      });
    });
  }, [jobs, generatingTracking]);

  // Active polling — fetch ONLY the generating jobs (1–3 typically) in
  // parallel and merge each result back through onPollingUpdate. Previously
  // this called loadJobs(true) every 2s, which refetched the entire current
  // phase view. For a user with 50–200 jobs in view, that's a 100–600 KB
  // payload every 2 seconds vs ~10–30 KB per generating job now.
  //
  // Effect deps are intentionally narrow: we only want to start/stop the
  // interval when the SET of jobs that should be polled changes. Updates to
  // those jobs' fields (which happen on every poll) read through refs.
  const activeIdsKey = (() => {
    const now = Date.now();
    return jobs
      .filter((j) => isActivelyGenerating(j, generatingTracking, pageLoadTime, now))
      .map((j) => j.jobid)
      .sort()
      .join(',');
  })();

  useEffect(() => {
    if (!activeIdsKey) return;

    const tick = async () => {
      if (document.hidden) return;
      const now = Date.now();
      const ids = jobsRef.current
        .filter((j) => isActivelyGenerating(j, trackingRef.current, pageLoadTime, now))
        .map((j) => j.jobid);
      if (ids.length === 0) return;

      const results = await Promise.allSettled(ids.map((id) => api.getJob(id)));
      const cb = onUpdateRef.current;
      if (!cb) return;
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) cb(r.value);
      }
    };

    const interval = setInterval(tick, 2000);
    return () => clearInterval(interval);
  }, [activeIdsKey, pageLoadTime]);

  // 30-second background polling for general data freshness — still a full
  // list refetch so it picks up server-side changes (counts drift, new jobs
  // from email ingest, etc.) outside the active-polling fast path.
  useEffect(() => {
    const POLL_INTERVAL_MS = 30_000;
    const interval = setInterval(() => {
      if (!document.hidden) {
        loadJobs(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadJobs]);

  return { generatingTracking, setGeneratingTracking };
}
